import { AlertTriangle, Info } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { cn } from "../../lib/cn";
import { Button } from "./button";
import { TextField } from "./form";
import { ModalShell } from "./modal-shell";

type ConfirmIntent = "info" | "destructive" | "danger-typed";

export interface ConfirmDialogInput {
  body: string;
  cancelLabel?: string;
  confirmLabel?: string;
  intent?: ConfirmIntent;
  requireText?: string;
  title: string;
}

interface ActiveConfirm extends ConfirmDialogInput {
  resolve(value: boolean): void;
}

interface ConfirmDialogContextValue {
  confirm(input: ConfirmDialogInput): Promise<boolean>;
}

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveConfirm | undefined>();
  const [typedText, setTypedText] = useState("");

  const settle = useCallback(
    (accepted: boolean) => {
      if (!active) return;
      active.resolve(accepted);
      setTypedText("");
      setActive(undefined);
    },
    [active],
  );
  const confirm = useCallback((input: ConfirmDialogInput) => {
    return new Promise<boolean>((resolve) => {
      setTypedText("");
      setActive({ ...input, resolve });
    });
  }, []);

  const value = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmDialogContext.Provider value={value}>
      {children}
      {active ? (
        <ModalShell
          backdropLabel="Cancel confirmation"
          closeLabel="Cancel confirmation"
          description={active.body}
          footer={
            <>
              <Button
                data-autofocus={active.intent === "info" ? undefined : "true"}
                onClick={() => settle(false)}
                variant="secondary"
              >
                {active.cancelLabel ?? "Cancel"}
              </Button>
              <Button
                data-autofocus={active.intent === "info" ? "true" : undefined}
                disabled={!canConfirm(active, typedText)}
                onClick={() => settle(true)}
                tone={active.intent === "info" ? "neutral" : "danger"}
                variant="primary"
              >
                {active.confirmLabel ?? "Confirm"}
              </Button>
            </>
          }
          icon={<ConfirmIcon intent={active.intent ?? "destructive"} />}
          onClose={() => settle(false)}
          open
          role="alertdialog"
          size="md"
          title={active.title}
        >
          {active.intent === "danger-typed" && active.requireText ? (
            <div className="px-[var(--layout-card-pad-x)] py-[var(--layout-card-pad-y)]">
              <TextField
                autoComplete="off"
                helper={
                  canConfirm(active, typedText)
                    ? "Match confirmed."
                    : "Match the value above exactly (whitespace ignored)."
                }
                label={`Type ${active.requireText} to confirm`}
                onChange={setTypedText}
                spellCheck={false}
                value={typedText}
              />
            </div>
          ) : null}
        </ModalShell>
      ) : null}
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog(): ConfirmDialogContextValue {
  const value = useContext(ConfirmDialogContext);
  if (!value) {
    throw new Error("ConfirmDialogProvider is missing");
  }
  return value;
}

function ConfirmIcon({ intent }: { intent: ConfirmIntent }) {
  const className = cn(
    "mt-0.5 flex size-[var(--icon-button-md)] shrink-0 items-center justify-center rounded-md",
    intent === "info"
      ? "bg-[var(--color-info-soft)] text-[var(--color-info)]"
      : "bg-[var(--color-danger-soft)] text-[var(--color-danger)]",
  );
  const Icon = intent === "info" ? Info : AlertTriangle;
  return (
    <span className={className}>
      <Icon aria-hidden="true" className="size-5" />
    </span>
  );
}

function canConfirm(active: ActiveConfirm, typedText: string): boolean {
  if (active.intent !== "danger-typed" || !active.requireText) return true;
  return typedText.trim() === active.requireText;
}
